import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Grid, IconButton, Menu, MenuItem, Typography, Stack } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate, useParams } from 'react-router-dom';
import { LeadFormActions } from '../../../Services/_actions/index.js';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import CommonTable from '../../Shared/Tables/TableV1.tsx';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { BackButton } from '../../Shared/BackButton/index.jsx';
import CommonBadges from '../../Shared/Badges/index.jsx';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { useRecoilState } from 'recoil';
import { campaignLeadManagementAtom } from '../../../Services/_states/index.js';
import StatusTriggersHelp from '../../Shared/HelpFIles/StatusTriggersHelp.tsx';

const List: React.FC = () => {
  const actions = LeadFormActions();
  const { campaignId, leadFormId } = useParams();
  const [campaignLeadManagementData, setCampaignLeadManagementData] = useRecoilState(
    campaignLeadManagementAtom
  );
  const navigate = useNavigate();
  const [statusList, setStatusList] = useState<any[]>([]);
  const [allStatusList, setAllStatusList] = useState<any[]>([]);
  const [statusFilters, setStatusFilters] = useState({
    active: '',
    leadFormId: leadFormId,
  });
  const [isLoading, setIsLaoding] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<any | null>(null);
  const { showSnackBar } = useSnackBar();
  const [typeFilter, setTypeFilter] = useState({ label: 'All', value: '' });
  const [updatedStatusOrder, setUpdatedStatusOrder] = useState<any[]>([]);
  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Campaigns', onClick: () => navigate('/r/records/campaigns') },
    { label: 'Forms', onClick: () => navigate(`/r/records/${campaignId}/form`) },
    { label: 'Status' },
  ];

  const handleEditClick = (rowData) => {
    navigate(`/r/records/status-update/${campaignId}/${leadFormId}/${rowData?.status_id}`, {
      state: {
        rowData: rowData,
        leadFormId,
      },
    });
  };
  const handleDelete = (row: any) => {
    setRowToDelete(row);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async (confirmed: boolean) => {
    const statusId = rowToDelete?.status_id ?? rowToDelete?.id ?? rowToDelete?.statusId;
    if (!statusId) {
      showSnackBar('Delete id not found on selected row', 'error');
    }

    try {
      setIsLaoding(true);
      const res = await actions.deleteStatus(statusId);

      if (res?.status === 200) {
        showSnackBar('Status deleted successfully', 'success');
        setStatusList((prev) =>
          prev.filter((item) => {
            const id = item?.status_id ?? item?.id ?? item?.statusId;
            return String(id) !== String(statusId);
          })
        );
      } else {
        showSnackBar('Status not deleted', 'error');
      }
    } catch (error) {
      showSnackBar('Something went wrong while deleting', 'error');
    } finally {
      setIsLaoding(false);
      setConfirmOpen(false);
      setRowToDelete(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'sort_num',
        header: '#',
        enableSorting: false,
        Cell: ({ row }: any) => row?.original?.sort_num,

        // Cell: ({ row }: any) => row.index + 1,
        size: 60,
      },
      {
        accessorKey: 'status_name',
        header: 'Status',
        enableSorting: false,
      },
      // {
      //   accessorKey: 'alias_name',
      //   header: 'Alias Name',
      //   enableSorting: false,
      // },
      {
        accessorKey: 'active',
        header: 'Active',
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <CommonBadges
              value={row?.original?.active ? 'Yes' : 'No'}
              color={row?.original?.active ? 'success' : 'error'}
              variant="outlined"
              size="small"
            />
            // <Chip label={row?.original?.active ? "Yes" : "No"} size='small' color={row?.original?.active ? "success" : "error"} variant="outlined" />
          );
        },
      },
      {
        accessorKey: 'isLeadClosed',
        header: 'Closed',
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <CommonBadges
              value={row?.original?.isLeadClosed ? 'Yes' : 'No'}
              color={row?.original?.isLeadClosed ? 'success' : 'error'}
              variant="outlined"
              size="small"
            />
          );
        },
      },
      {
        accessorKey: 'type_of_status',
        header: 'Type of status',
        enableSorting: false,
      },
      {
        accessorKey: 'substatuses',
        header: 'Sub Statuses',
        enableSorting: false,
        Cell: ({ row }) => {
          const substatuses =
            row.original?.substatuses?.map((item: any) => item?.name)?.filter(Boolean) || [];
          const [expanded, setExpanded] = React.useState(false);
          const handleToggle = () => setExpanded(!expanded);

          const visibleList = expanded ? substatuses : substatuses.slice(0, 3);
          const hasMore = substatuses.length > 3;

          return (
            <Box>
              {visibleList.map((name, index) => (
                <div key={index}>{name}</div>
              ))}
              {hasMore && (
                <Box sx={{ color: 'blue', cursor: 'pointer', mt: 0.5 }} onClick={handleToggle}>
                  {expanded ? 'Show less' : `+${substatuses.length - 3} more`}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: 'action',
        header: 'Action',
        enableSorting: false,
        size: 60,
        Cell: ({ row }: any) => {
          const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
          const open = Boolean(anchorEl);

          const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
          };

          const handleClose = () => {
            setAnchorEl(null);
          };

          return (
            <div>
              <IconButton onClick={handleClick}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                id={`menu-${row.index}`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': `menu-button-${row.index}`,
                }}
              >
                <MenuItem onClick={() => handleEditClick(row.original)}>Edit</MenuItem>
                <MenuItem
                  onClick={() => {
                    handleDelete(row.original);
                    handleClose();
                  }}
                >
                  Delete
                </MenuItem>
              </Menu>
            </div>
          );
        },
      },
    ],
    [campaignId]
  );

  const fetchStatusAndSubStatus = async (filterData) => {
    setIsLaoding(true);
    try {
      const res = await actions.fetchStatusAndSubStatus(filterData);
      if (res?.status === 200) {
        setCampaignLeadManagementData((prev) => ({
          ...prev,
          statusData: {
            ...(prev?.statusData ?? {}),
            [leadFormId]: res?.data ?? [],
          },
        }));

        // If fetching "All", update both
        if (!filterData?.type_of_status || filterData?.type_of_status === 'all') {
          setAllStatusList(res?.data ?? []);
        }

        setStatusList(res?.data ?? []);
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setIsLaoding(false);
    }
  };

  const handleSelectStatus = (e) => {
    const updateFilters = { ...statusFilters, active: e };
    setStatusFilters(updateFilters);
    fetchStatusAndSubStatus(updateFilters);
  };
  useEffect(() => {
    fetchStatusAndSubStatus(statusFilters);
  }, []);

  const getTypeOptionsFromStatusList = () => {
    const uniqueTypes = Array.from(
      new Set(allStatusList.map((s) => s.type_of_status).filter(Boolean))
    );

    return [
      { label: 'All', value: 'all' },
      ...uniqueTypes.map((type) => ({ label: type, value: type })),
    ];
  };


  const handleSelectType = (selectedOption: any) => {
    setTypeFilter(selectedOption);

    let typeValue = selectedOption?.value;

    if (typeValue === 'all') {
      fetchStatusAndSubStatus({ ...statusFilters, type_of_status: '' });
    } else if (typeValue === '__LEAD_STATUS__') {
      fetchStatusAndSubStatus({ ...statusFilters, type_of_status: 'default' });
    } else {
      fetchStatusAndSubStatus({ ...statusFilters, type_of_status: typeValue });
    }
  };

  const handleSubmitOrder = async () => {
    if (!updatedStatusOrder || updatedStatusOrder.length === 0) return;

    const payload = updatedStatusOrder.map((item, index) => ({
      status_id: item.status_id,
      sort_num: index + 1,
    }));

    try {
      setIsLaoding(true);
      const res = await actions.updateStatusList(payload);
      if (res?.status === 200) {
        showSnackBar('Status order updated successfully', 'success');
        setStatusList(updatedStatusOrder);
        setUpdatedStatusOrder([]);
      } else {
        showSnackBar('Failed to update status order', 'error');
      }
    } catch (error) {
      console.error(error);
      showSnackBar('Something went wrong while updating order', 'error');
    } finally {
      setIsLaoding(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '8px' }}>
      {/* <DynamicBreadcrumbs items={breadcrumbsData} onBreadcrumbClick={(e, item) => item?.onClick?.()} /> */}
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Status
          </Typography>
        </Grid>
        <Grid item>
          <Grid item>
            <Stack direction="row" spacing={1} alignItems="center">
              
              <BackButton onClick={() => navigate(-1)} />
                <StatusTriggersHelp />
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center" sx={{ pb: 2 }}>
        <Grid item xs={12} sm={6} md={2}>
          <SearchableDropdown
            options={getTypeOptionsFromStatusList()}
            value={typeFilter}
            onChange={handleSelectType}
            label="Type of Status"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <SearchableDropdown
            options={[
              { label: 'All', value: 'all' },
              { label: 'Active', value: 'true' },
              { label: 'Inactive', value: 'false' },
            ]}
            value={statusFilters?.active || null}
            onChange={(e) => handleSelectStatus(e)}
            label="Select Status"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/r/records/status-create/${campaignId}/${leadFormId}`)}
            >
              Create
            </Button>
            {updatedStatusOrder.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                disabled={isLoading}
                onClick={handleSubmitOrder}
              >
                Submit Order
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
      {/* <CommonTable
        columns={columns}
        data={statusList}
        enableBottomToolbar={false}
        muiTableContainerProps={{
          sx: {
            height: 'calc(100vh - 280px)',
            width: '100%',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
          },
        }}
        state={{ isLoading: isLoading }}
      /> */}
      <CommonTable
        columns={columns}
        data={statusList}
        enableRowOrdering={true}
        muiRowDragHandleProps={({ table }) => ({
          onDragEnd: () => {
            const { draggingRow, hoveredRow } = table.getState();
            if (draggingRow && hoveredRow) {
              const newData = [...statusList];
              newData.splice(hoveredRow.index, 0, newData.splice(draggingRow.index, 1)[0]);
              setStatusList(newData);
              setUpdatedStatusOrder(newData);
            }
          },
        })}
        muiTableContainerProps={{
          sx: {
            height: 'calc(100vh - 280px)',
            width: '100%',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
          },
        }}
        state={{ isLoading }}
      />

      <ConfirmBox
        open={confirmOpen}
        onClose={handleConfirmDelete}
        options={{
          title: 'Delete Form',
          text: 'Are you sure you want to delete this Status?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',
        }}
      />
    </Box>
  );
};

export default List;
